import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { favoritesAPI, attractionsAPI } from '../services/api';
import './Favorites.css';

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'view'
  const [selectedFavorite, setSelectedFavorite] = useState(null);
  const [formData, setFormData] = useState({
    user_id: 1,
    item_id: '',
    type: 'attraction'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    hasMore: false
  });
  const [filters, setFilters] = useState({
    type: ''
  });

  useEffect(() => {
    fetchFavorites();
    fetchAttractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchFavorites = async (page = 1) => {
    try {
      setLoading(true);
      const response = await favoritesAPI.getAll(1, filters.type || null, page, 20);
      
      if (page === 1) {
        setFavorites(response.data.favorites);
      } else {
        setFavorites(prev => [...prev, ...response.data.favorites]);
      }
      
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttractions = async () => {
    try {
      const response = await attractionsAPI.getAll();
      setAttractions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch attractions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await favoritesAPI.add(formData.item_id, formData.type, formData.user_id);
      alert('Item added to favorites successfully!');
      
      setShowModal(false);
      resetForm();
      fetchFavorites();
    } catch (error) {
      console.error('Failed to add to favorites:', error);
      
      if (error.response?.status === 409) {
        alert('This item is already in your favorites!');
      } else {
        alert('Failed to add to favorites. Please try again.');
      }
    }
  };

  const handleDelete = async (favoriteId) => {
    if (window.confirm('Are you sure you want to remove this item from favorites?')) {
      try {
        await favoritesAPI.remove(favoriteId);
        alert('Item removed from favorites successfully!');
        fetchFavorites();
      } catch (error) {
        console.error('Failed to remove from favorites:', error);
        alert('Failed to remove from favorites. Please try again.');
      }
    }
  };

  const handleView = (favorite) => {
    setSelectedFavorite(favorite);
    setModalType('view');
    setShowModal(true);
  };

  const handleAddNew = () => {
    setModalType('add');
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      user_id: 1,
      item_id: '',
      type: 'attraction'
    });
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedFavorite(null);
    resetForm();
  };

  const loadMoreFavorites = async () => {
    if (pagination.hasMore) {
      fetchFavorites(pagination.page + 1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getItemDisplayName = (favorite) => {
    return favorite.item_name || favorite.item_id;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'attraction': return '🎯';
      case 'hotel': return '🏨';
      case 'itinerary': return '📋';
      default: return '⭐';
    }
  };

  if (loading && favorites.length === 0) {
    return (
      <Layout title="Favorites Management">
        <div className="loading">Loading favorites...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Favorites Management">
      <div className="favorites-management">
        <div className="page-header">
          <h2>Favorites Management</h2>
          <div className="header-actions">
            <span className="total-count">{pagination.total} total favorites</span>
            <button 
              className="create-btn"
              onClick={handleAddNew}
            >
              + Add to Favorites
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filter-group">
            <label>Filter by Type:</label>
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="attraction">Attractions</option>
              <option value="hotel">Accommodation</option>
              <option value="itinerary">Itineraries</option>
            </select>
          </div>
        </div>

        <div className="favorites-table-container">
          <table className="favorites-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Type</th>
                <th>Item Name</th>
                <th>Category</th>
                <th>Description</th>
                <th>Price</th>
                <th>Rating</th>
                <th>Added Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {favorites.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">No favorites found</td>
                </tr>
              ) : (
                favorites.map((favorite) => (
                  <tr key={favorite.id}>
                    <td>
                      <span className="user-id-badge">
                        {favorite.user_id}
                      </span>
                    </td>
                    <td>
                      <span className="type-badge">
                        {getTypeIcon(favorite.type)} {favorite.type}
                      </span>
                    </td>
                    <td>
                      <div className="item-name">
                        <span className="name">{getItemDisplayName(favorite)}</span>
                      </div>
                    </td>
                    <td>{favorite.item_category || 'N/A'}</td>
                    <td className="description-cell">
                      {favorite.item_description 
                        ? (favorite.item_description.length > 100 
                           ? `${favorite.item_description.substring(0, 100)}...` 
                           : favorite.item_description)
                        : 'N/A'
                      }
                    </td>
                    <td>
                      {favorite.item_price 
                        ? `${favorite.item_currency || 'MYR'} ${favorite.item_price}`
                        : 'N/A'
                      }
                    </td>
                    <td>
                      {favorite.item_rating 
                        ? `${favorite.item_rating}/5`
                        : 'N/A'
                      }
                    </td>
                    <td>{formatDate(favorite.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="view-btn"
                          onClick={() => handleView(favorite)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDelete(favorite.id)}
                          title="Remove from Favorites"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.hasMore && (
          <div className="load-more-container">
            <button 
              className="load-more-btn"
              onClick={loadMoreFavorites}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More Favorites'}
            </button>
          </div>
        )}

        <div className="pagination-info">
          Showing {favorites.length} of {pagination.total} favorites
        </div>

        {/* Modal for Add to Favorites or View Details */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>
                  {modalType === 'add' ? 'Add to Favorites' : 'Favorite Details'}
                </h3>
                <button className="close-btn" onClick={handleCancel}>×</button>
              </div>

              {modalType === 'add' ? (
                <form onSubmit={handleSubmit} className="favorites-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>User ID *</label>
                      <input
                        type="number"
                        name="user_id"
                        value={formData.user_id}
                        onChange={handleInputChange}
                        placeholder="Enter User ID"
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Type *</label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="attraction">Attraction</option>
                        <option value="hotel">Accommodation</option>
                        <option value="itinerary">Itinerary</option>
                      </select>
                    </div>

                    <div className="form-group full-width">
                      <label>Item *</label>
                      {formData.type === 'attraction' ? (
                        <select
                          name="item_id"
                          value={formData.item_id}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select an attraction</option>
                          {attractions.map((attraction) => (
                            <option key={attraction.id} value={attraction.id}>
                              {attraction.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          name="item_id"
                          value={formData.item_id}
                          onChange={handleInputChange}
                          placeholder={`Enter ${formData.type} ID`}
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="cancel-btn">
                      Cancel
                    </button>
                    <button type="submit" className="submit-btn">
                      Add to Favorites
                    </button>
                  </div>
                </form>
              ) : (
                <div className="favorite-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Type:</label>
                      <span className="type-badge">
                        {getTypeIcon(selectedFavorite?.type)} {selectedFavorite?.type}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Item Name:</label>
                      <span>{getItemDisplayName(selectedFavorite)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Category:</label>
                      <span>{selectedFavorite?.item_category || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>District:</label>
                      <span>{selectedFavorite?.item_district || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Price:</label>
                      <span>
                        {selectedFavorite?.item_price 
                          ? `${selectedFavorite?.item_currency || 'MYR'} ${selectedFavorite?.item_price}`
                          : 'N/A'
                        }
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Rating:</label>
                      <span>
                        {selectedFavorite?.item_rating 
                          ? `${selectedFavorite?.item_rating}/5`
                          : 'N/A'
                        }
                      </span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Description:</label>
                      <span>{selectedFavorite?.item_description || 'No description available'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Added Date:</label>
                      <span>{formatDate(selectedFavorite?.created_at)}</span>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="cancel-btn">
                      Close
                    </button>
                    <button 
                      type="button" 
                      onClick={() => handleDelete(selectedFavorite?.id)}
                      className="delete-btn-modal"
                    >
                      Remove from Favorites
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Favorites;